/*
 * Recipes library (sections/recipes-library.liquid).
 *
 * The cards, filter buttons and counts are all rendered on the server. This file only:
 *   - toggles `hidden` on cards and recomputes counts when a filter, the search or the view changes
 *   - mirrors the state in the URL with history.replaceState, and reads it back on load:
 *       ?diet=gluten-free,dairy-free&flavor=vanilla&type=breakfast-oats&time=under-10-min
 *        &kitchen=no-oven&q=banana&view=list
 *     slugs come from each button's data-slug, so the URL and the markup cannot drift apart
 *   - opens / closes the filter panel as a bottom sheet below 990px
 *
 * Matching: AND across groups, OR within a group. Dietary needs is AND: every ticked allergen
 * must be absent from the card's data-contains. Time is single-select. "No oven" = not Oven.
 */
(function () {
  if (customElements.get('recipes-library')) return;

  var GROUPS = ['diet', 'flavor', 'type', 'time', 'kitchen'];
  var MOBILE = window.matchMedia('(max-width: 989px)');

  function debounce(fn, ms) {
    var t;
    return function () {
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () {
        fn.apply(null, args);
      }, ms);
    };
  }

  class RecipesLibrary extends HTMLElement {
    connectedCallback() {
      this.total = Number(this.dataset.total) || 0;
      this.state = { diet: [], flavor: [], type: [], time: [], kitchen: [], q: '', view: 'grid' };

      this.buttons = Array.from(this.querySelectorAll('[data-group]'));
      this.quick = Array.from(this.querySelectorAll('[data-quick]'));
      this.viewButtons = Array.from(this.querySelectorAll('[data-view]'));
      this.search = this.querySelector('[data-search]');
      this.results = this.querySelector('[data-results]');
      this.empty = this.querySelector('[data-empty]');
      this.countLabel = this.querySelector('[data-count-label]');
      this.showLabel = this.querySelector('[data-show-label]');
      this.filterLabel = this.querySelector('[data-filter-label]');
      this.clearButtons = Array.from(this.querySelectorAll('[data-clear]'));
      this.sheet = this.querySelector('[data-filters]');
      this.sheetOpenButton = this.querySelector('[data-sheet-open]');

      this.cards = Array.from(this.querySelectorAll('[data-recipe]')).map(function (el) {
        return {
          el: el,
          flavor: el.dataset.flavor,
          type: el.dataset.type,
          time: el.dataset.time,
          kitchen: el.dataset.kitchen,
          contains: el.dataset.contains ? el.dataset.contains.split('|') : [],
          search: el.dataset.search || ''
        };
      });

      this.readUrl();
      this.bind();
      this.setPlaceholder();
      this.update(false);
      this.measureStickyTop();
    }

    /* ---------- events ---------- */

    bind() {
      var self = this;

      this.buttons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.toggle(btn.dataset.group, btn.dataset.value, btn.hasAttribute('data-single'));
        });
      });

      this.quick.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.toggle('diet', btn.dataset.quick, false);
        });
      });

      this.viewButtons.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.state.view = btn.dataset.view;
          self.update(true);
        });
      });

      this.clearButtons.concat(Array.from(this.querySelectorAll('[data-clear-empty]'))).forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.clearAll();
        });
      });

      if (this.search) {
        this.search.value = this.state.q;
        var onSearch = debounce(function () {
          self.state.q = self.search.value;
          self.update(true);
        }, 120);
        this.search.addEventListener('input', onSearch);
        this.search.addEventListener('search', onSearch);
      }

      if (this.sheetOpenButton) {
        this.sheetOpenButton.addEventListener('click', function () {
          self.openSheet();
        });
      }
      this.querySelectorAll('[data-sheet-close]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.closeSheet();
        });
      });
      this.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.sheet.classList.contains('is-open')) self.closeSheet();
      });

      var onResize = debounce(function () {
        self.setPlaceholder();
        self.measureStickyTop();
        self.renderCount();
        if (!MOBILE.matches) self.closeSheet(true);
      }, 150);
      window.addEventListener('resize', onResize);
    }

    toggle(group, value, single) {
      var list = this.state[group];
      var i = list.indexOf(value);
      if (single) {
        this.state[group] = i > -1 ? [] : [value];
      } else if (i > -1) {
        list.splice(i, 1);
      } else {
        list.push(value);
      }
      this.update(true);
    }

    clearAll() {
      var self = this;
      GROUPS.forEach(function (g) {
        self.state[g] = [];
      });
      this.state.q = '';
      if (this.search) this.search.value = '';
      this.update(true);
    }

    /* ---------- matching ---------- */

    matches(card, skip) {
      var s = this.state;
      var q = s.q.trim().toLowerCase();
      if (q && card.search.indexOf(q) === -1) return false;
      if (skip !== 'diet' && s.diet.some(function (a) { return card.contains.indexOf(a) > -1; })) return false;
      if (skip !== 'flavor' && s.flavor.length && s.flavor.indexOf(card.flavor) === -1) return false;
      if (skip !== 'type' && s.type.length && s.type.indexOf(card.type) === -1) return false;
      if (skip !== 'time' && s.time.length && s.time.indexOf(card.time) === -1) return false;
      if (
        skip !== 'kitchen' &&
        s.kitchen.length &&
        !s.kitchen.some(function (k) {
          return k === 'No oven' ? card.kitchen !== 'Oven' : card.kitchen === k;
        })
      )
        return false;
      return true;
    }

    activeCount() {
      var s = this.state;
      return s.diet.length + s.flavor.length + s.type.length + s.time.length + s.kitchen.length;
    }

    /* ---------- render ---------- */

    update(writeUrl) {
      var self = this;
      var s = this.state;

      this.visible = 0;
      this.cards.forEach(function (card) {
        var show = self.matches(card);
        card.el.hidden = !show;
        if (show) self.visible++;
      });

      this.buttons.forEach(function (btn) {
        var on = s[btn.dataset.group].indexOf(btn.dataset.value) > -1;
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (btn.dataset.group === 'diet') {
          var a = btn.dataset.value;
          var n = self.cards.filter(function (c) {
            return self.matches(c, 'diet') && c.contains.indexOf(a) === -1;
          }).length;
          var count = btn.querySelector('[data-count]');
          if (count) count.textContent = n;
        }
      });

      this.quick.forEach(function (btn) {
        btn.setAttribute('aria-pressed', s.diet.indexOf(btn.dataset.quick) > -1 ? 'true' : 'false');
      });

      this.viewButtons.forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.dataset.view === s.view ? 'true' : 'false');
      });
      this.results.classList.toggle('rcp-results--list', s.view === 'list');

      var hasFilters = this.activeCount() > 0 || s.q.trim() !== '';
      this.clearButtons.forEach(function (btn) {
        btn.hidden = !hasFilters;
      });

      this.empty.hidden = this.visible !== 0 || this.cards.length === 0;
      this.renderCount();

      var n = this.activeCount();
      if (this.filterLabel) this.filterLabel.textContent = n ? 'Filters · ' + n : 'Filters';
      if (this.showLabel) {
        this.showLabel.textContent = 'Show ' + this.visible + (this.visible === 1 ? ' recipe' : ' recipes');
      }

      if (writeUrl) this.writeUrl();
    }

    renderCount() {
      if (!this.countLabel) return;
      var v = this.visible;
      var noun = v === 1 ? ' recipe' : ' recipes';
      if (MOBILE.matches) {
        this.countLabel.textContent = v + noun;
      } else {
        this.countLabel.textContent =
          v === this.cards.length ? 'All ' + v + noun : v + ' of ' + this.cards.length + ' recipes';
      }
    }

    setPlaceholder() {
      if (!this.search) return;
      if (!this.search.dataset.placeholderDesktop) this.search.dataset.placeholderDesktop = this.search.placeholder;
      var mobile = this.search.dataset.placeholderMobile;
      this.search.placeholder = MOBILE.matches && mobile ? mobile : this.search.dataset.placeholderDesktop;
    }

    /* ---------- URL ---------- */

    slugMap(group) {
      var map = {};
      this.buttons.forEach(function (btn) {
        if (btn.dataset.group === group) map[btn.dataset.slug] = btn.dataset.value;
      });
      return map;
    }

    readUrl() {
      var self = this;
      var params = new URLSearchParams(window.location.search);
      GROUPS.forEach(function (g) {
        var raw = params.get(g);
        if (!raw) return;
        var map = self.slugMap(g);
        var values = raw
          .split(',')
          .map(function (slug) {
            return map[slug.trim().toLowerCase()];
          })
          .filter(Boolean);
        if (g === 'time') values = values.slice(0, 1);
        self.state[g] = values.filter(function (v, i) {
          return values.indexOf(v) === i;
        });
      });
      this.state.q = params.get('q') || '';
      this.state.view = params.get('view') === 'list' ? 'list' : 'grid';
    }

    writeUrl() {
      var self = this;
      var url = new URL(window.location.href);
      GROUPS.forEach(function (g) {
        var slugs = self.buttons
          .filter(function (b) {
            return b.dataset.group === g && self.state[g].indexOf(b.dataset.value) > -1;
          })
          .map(function (b) {
            return b.dataset.slug;
          });
        if (slugs.length) url.searchParams.set(g, slugs.join(','));
        else url.searchParams.delete(g);
      });
      var q = this.state.q.trim();
      if (q) url.searchParams.set('q', q);
      else url.searchParams.delete('q');
      if (this.state.view === 'list') url.searchParams.set('view', 'list');
      else url.searchParams.delete('view');
      var next = url.pathname + (url.searchParams.toString() ? '?' + url.searchParams.toString() : '') + url.hash;
      history.replaceState(history.state, '', next.replace(/%2C/g, ','));
    }

    /* ---------- bottom sheet ---------- */

    openSheet() {
      this.lastFocus = document.activeElement;
      this.sheet.classList.add('is-open');
      this.sheet.setAttribute('role', 'dialog');
      this.sheet.setAttribute('aria-modal', 'true');
      document.documentElement.classList.add('rcp-lock');
      if (this.sheetOpenButton) this.sheetOpenButton.setAttribute('aria-expanded', 'true');
      var close = this.sheet.querySelector('.rcp-filters__close');
      if (close) close.focus();
    }

    closeSheet(silent) {
      if (!this.sheet.classList.contains('is-open')) return;
      this.sheet.classList.remove('is-open');
      this.sheet.removeAttribute('role');
      this.sheet.removeAttribute('aria-modal');
      document.documentElement.classList.remove('rcp-lock');
      if (this.sheetOpenButton) this.sheetOpenButton.setAttribute('aria-expanded', 'false');
      if (!silent && this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      if (!silent && MOBILE.matches) {
        var top = this.results.getBoundingClientRect().top + window.scrollY - this.stickyOffset() - 90;
        if (window.scrollY > top) window.scrollTo({ top: top });
      }
    }

    /* ---------- sticky offset under a sticky theme header ---------- */

    stickyOffset() {
      var header = document.querySelector('.section-header, .shopify-section-header-sticky');
      if (!header) return 0;
      var pos = getComputedStyle(header).position;
      if (pos !== 'sticky' && pos !== 'fixed') return 0;
      var rect = header.getBoundingClientRect();
      return rect.bottom > 0 ? Math.round(rect.height) : 0;
    }

    measureStickyTop() {
      this.style.setProperty('--rcp-sticky-top', this.stickyOffset() + 'px');
    }
  }

  customElements.define('recipes-library', RecipesLibrary);
})();
